export type CadTileCoordinate = { x: number; y: number; z: number };
export type CadTileLease = {
  promise: Promise<HTMLCanvasElement | null>;
  cancel: () => boolean;
};
type Entry = {
  key: string;
  tile: CadTileCoordinate;
  controller: AbortController;
  subscribers: Set<(value: HTMLCanvasElement | null) => void>;
  state: 'queued' | 'loading' | 'ready';
  canvas?: HTMLCanvasElement;
  lastUsed: number;
  pinned: boolean;
};

export const cadTileKey = ({ x, y, z }: CadTileCoordinate) => `${z}/${x}/${y}`;
export const isFatalCadError = (error: unknown) =>
  [401, 403, 404, 409].includes(
    (error as { status?: number } | null)?.status ?? 0,
  );

export class CompoundCadTileCache {
  private readonly entries = new Map<string, Entry>();
  private queue: Entry[] = [];
  private active = 0;
  private disposed = false;
  private clock = 0;
  private hits = 0;
  private loaded = 0;
  private evictions = 0;

  constructor(
    private readonly load: (
      tile: CadTileCoordinate,
      signal: AbortSignal,
    ) => Promise<HTMLCanvasElement>,
    private readonly options: {
      onError: (error: Error) => void;
      onLoaded?: (tile: CadTileCoordinate, pinned: boolean) => void;
    },
  ) {}

  request(tile: CadTileCoordinate, pinned = false): CadTileLease {
    if (this.disposed)
      return { promise: Promise.resolve(null), cancel: () => false };
    const key = cadTileKey(tile);
    let entry = this.entries.get(key);
    if (!entry) {
      if (
        this.queue.length >= 256 ||
        (pinned &&
          [...this.entries.values()].filter((candidate) => candidate.pinned)
            .length >= 16)
      )
        return { promise: Promise.resolve(null), cancel: () => false };
      entry = {
        key,
        tile,
        controller: new AbortController(),
        subscribers: new Set(),
        state: 'queued',
        lastUsed: ++this.clock,
        pinned,
      };
      this.entries.set(key, entry);
      this.queue.push(entry);
    }
    entry.lastUsed = ++this.clock;
    if (entry.canvas) {
      this.hits += 1;
      return {
        promise: Promise.resolve(entry.canvas),
        cancel: () => false,
      };
    }
    let settle!: (value: HTMLCanvasElement | null) => void;
    const promise = new Promise<HTMLCanvasElement | null>((resolve) => {
      settle = resolve;
    });
    entry.subscribers.add(settle);
    const target = entry;
    let cancelled = false;
    this.pump();
    return {
      promise,
      cancel: () => {
        if (cancelled) return false;
        cancelled = true;
        if (!target.subscribers.delete(settle)) return false;
        settle(null);
        if (
          target.state === 'queued' &&
          !target.subscribers.size &&
          this.removeEntry(target)
        ) {
          this.queue = this.queue.filter((candidate) => candidate !== target);
          return true;
        }
        return false;
      },
    };
  }

  peek(tile: CadTileCoordinate) {
    const entry = this.entries.get(cadTileKey(tile));
    if (!entry?.canvas) return undefined;
    entry.lastUsed = ++this.clock;
    this.hits += 1;
    return entry.canvas;
  }

  cancelQueuedOutside(keep: (tile: CadTileCoordinate) => boolean) {
    const cancelled: CadTileCoordinate[] = [];
    this.queue = this.queue.filter((entry) => {
      if (entry.pinned || keep(entry.tile)) return true;
      const removed = this.removeEntry(entry);
      this.settle(entry, null);
      if (removed) cancelled.push(entry.tile);
      return false;
    });
    return cancelled;
  }

  diagnostics() {
    const ready = [...this.entries.values()].filter((entry) => entry.canvas);
    const details = ready.filter((entry) => !entry.pinned);
    const overview = ready.filter((entry) => entry.pinned);
    const bytes = (entries: Entry[]) =>
      entries.reduce(
        (total, entry) =>
          total + entry.canvas!.width * entry.canvas!.height * 4,
        0,
      );
    return {
      cachedDetails: details.length,
      detailBytes: bytes(details),
      detailTileLimit: 256,
      detailByteLimit: 64 * 1024 * 1024,
      cachedOverview: overview.length,
      overviewBytes: bytes(overview),
      overviewTileLimit: 16,
      overviewByteLimit: 4 * 1024 * 1024,
      activeLoads: this.disposed ? 0 : this.active,
      concurrencyLimit: 6,
      queued: this.queue.length,
      queueLimit: 256,
      cacheHits: this.hits,
      loadedTiles: this.loaded,
      evictions: this.evictions,
    };
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.queue = [];
    for (const entry of this.entries.values()) {
      entry.controller.abort();
      this.settle(entry, null);
      if (entry.canvas) entry.canvas.width = entry.canvas.height = 0;
    }
    this.entries.clear();
  }

  private settle(entry: Entry, canvas: HTMLCanvasElement | null) {
    for (const resolve of entry.subscribers) resolve(canvas);
    entry.subscribers.clear();
  }

  private removeEntry(entry: Entry) {
    if (this.entries.get(entry.key) !== entry) return false;
    return this.entries.delete(entry.key);
  }

  private pump() {
    while (!this.disposed && this.active < 6 && this.queue.length) {
      const entry = this.queue.shift()!;
      entry.state = 'loading';
      this.active += 1;
      void this.loadWithRetry(entry)
        .then((canvas) => {
          if (this.disposed) {
            canvas.width = canvas.height = 0;
            return;
          }
          if (entry.pinned) {
            const overviewBytes = [...this.entries.values()].reduce(
              (bytes, candidate) =>
                bytes +
                (candidate.pinned && candidate.canvas
                  ? candidate.canvas.width * candidate.canvas.height * 4
                  : 0),
              0,
            );
            if (
              overviewBytes + canvas.width * canvas.height * 4 >
              4 * 1024 * 1024
            ) {
              canvas.width = canvas.height = 0;
              throw new Error('CAD overview exceeds its decoded pixel budget.');
            }
          }
          entry.state = 'ready';
          entry.canvas = canvas;
          this.loaded += 1;
          this.evict();
          this.settle(entry, canvas);
          this.options.onLoaded?.(entry.tile, entry.pinned);
        })
        .catch((error: unknown) => {
          this.removeEntry(entry);
          this.settle(entry, null);
          if (!this.disposed) {
            if (isFatalCadError(error)) this.dispose();
            this.options.onError(
              error instanceof Error ? error : new Error('CAD request failed.'),
            );
          }
        })
        .finally(() => {
          this.active -= 1;
          this.pump();
        });
    }
  }

  private async loadWithRetry(entry: Entry): Promise<HTMLCanvasElement> {
    for (let attempt = 0; ; attempt += 1) {
      const controller =
        attempt === 0 ? entry.controller : new AbortController();
      entry.controller = controller;
      const { signal } = controller;
      const timeout = setTimeout(
        () =>
          controller.abort(
            new DOMException('CAD tile load timed out.', 'TimeoutError'),
          ),
        15_000,
      );
      const onAbort = () => clearTimeout(timeout);
      signal.addEventListener('abort', onAbort, { once: true });
      try {
        const canvas = await this.load(entry.tile, signal);
        if (signal.aborted) {
          canvas.width = canvas.height = 0;
          signal.throwIfAborted();
        }
        return canvas;
      } catch (error) {
        if (
          this.disposed ||
          isFatalCadError(error) ||
          attempt >= 1 ||
          !entry.subscribers.size
        )
          throw error;
      } finally {
        clearTimeout(timeout);
        signal.removeEventListener('abort', onAbort);
      }
    }
  }

  private evict() {
    const ready = [...this.entries.values()]
      .filter((entry) => entry.canvas && !entry.pinned)
      .sort((a, b) => a.lastUsed - b.lastUsed);
    let bytes = ready.reduce(
      (total, entry) => total + entry.canvas!.width * entry.canvas!.height * 4,
      0,
    );
    while (ready.length > 256 || bytes > 64 * 1024 * 1024) {
      const oldest = ready.shift()!;
      bytes -= oldest.canvas!.width * oldest.canvas!.height * 4;
      if (this.removeEntry(oldest)) this.evictions += 1;
    }
  }
}
